import { http as oldHttp } from '@/lib/http';
import { useQuery } from '@tanstack/react-query';

/**
 * Account-level settings (`GET /api/v1/account`).
 *
 * Lives here rather than in `src/pages/settings/api/api.ts` because the
 * requisition form — a `components/` module — needs the account's default
 * currency, and `components` may not import from `pages` (eslint boundaries).
 * The settings module re-exports these for its own callers.
 */
export interface AccountSettings {
  display_name?: string;
  time_zone?: string;
  date_fmt?: string;
  time_fmt?: string;
  /**
   * Country id of the account's default currency. Always an id from
   * `GET /api_ats/v1/compensations/currencies` — the backend validates writes
   * against exactly that (deduplicated) set. `null` when never configured.
   */
  default_currency_country_id?: number | null;
  /** Display label, e.g. "Indian Rupee (INR)". Read-only. */
  default_currency?: string | null;
  [key: string]: unknown;
}

const getAccount = async (): Promise<AccountSettings> => {
  const response = await oldHttp.get('/account');
  return response.data;
};

const updateAccount = async (payload: FormData | Record<string, unknown>) => {
  const response = await oldHttp.put('/account', payload);
  return response.data;
};

export const ACCOUNT_QUERY_KEYS = {
  account: () => ['account'] as const,
};

export const ACCOUNT_API_SERVICE = {
  getAccount,
  updateAccount,
};

export const useAccountQuery = () =>
  useQuery({
    queryKey: ACCOUNT_QUERY_KEYS.account(),
    queryFn: getAccount,
    staleTime: 0,
  });
